/**
 * Sentry Configuration for Picasso
 * 
 * This module initializes Sentry for error tracking across different environments.
 * Supports: testing, staging, production
 */

import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for error tracking
 * 
 * Environment variables required:
 * - VITE_SENTRY_DSN: Sentry Data Source Name
 * - VITE_APP_ENV: Environment (testing | staging | production)
 * - VITE_APP_VERSION: Application version
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_APP_ENV || 'deployed';
  const release = import.meta.env.VITE_APP_VERSION || '1.0.0';

  // Only initialize if DSN is provided
  if (!dsn) {
    console.warn('Sentry DSN not provided. Error tracking is disabled.');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release: `picasso-frontend@${release}`,

    // Send default PII data (IP address, user info)
    sendDefaultPii: true,

    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Session Replay - capture 10% of sessions, 100% of error sessions
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Set tracesSampleRate based on environment
    // Production: 100% of transactions, Others: 20%
    tracesSampleRate: environment === 'production' ? 1 : 0.2,

    // Capture Replay for 10% of sessions, 100% of sessions with errors
    replaysSessionSampleRate: environment === 'production' ? 0.1 : 0,
    replaysOnErrorSampleRate: 1,

    // Before sending events, scrub sensitive data
    beforeSend(event, hint) {
      // Remove sensitive data from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
          if (breadcrumb.data) {
            // Remove tokens, passwords, etc.
            const sanitized = { ...breadcrumb.data };
            ['password', 'token', 'authorization', 'cookie'].forEach(key => {
              if (key in sanitized) {
                sanitized[key] = '[Filtered]';
              }
            });
            return { ...breadcrumb, data: sanitized };
          }
          return breadcrumb;
        });
      }

      // Log to console in non-production
      if (environment !== 'production') {
        console.error('Sentry Event:', event, hint);
      }

      return event;
    },

    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      // Random plugins/extensions
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      // Facebook
      'fb_xd_fragment',
      // Network errors that are expected
      'NetworkError',
      'Network request failed',
      // User canceled actions
      'AbortError',
    ],

    // Ignore certain URLs
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Firefox extensions
      /^moz-extension:\/\//i,
    ],
  });

  console.log(`✅ Sentry initialized for environment: ${environment}, release: ${release}`);
}

/**
 * Set user context for Sentry
 * @param email - User email
 * @param id - User ID
 */
export function setSentryUser(email: string | null, id?: string) {
  if (email) {
    Sentry.setUser({
      email,
      id: id || email,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Add breadcrumb for tracking user actions
 * @param message - Description of the action
 * @param category - Category (navigation, user, etc.)
 * @param level - Severity level
 * @param data - Additional data
 */
export function addBreadcrumb(
  message: string,
  category: string = 'user',
  level: 'info' | 'warning' | 'error' = 'info',
  data?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
}

/**
 * Manually capture an exception
 * @param error - Error to capture
 * @param context - Additional context
 */
export function captureException(error: Error, context?: Record<string, unknown>) {
  if (context) {
    Sentry.setContext('additional', context);
  }
  Sentry.captureException(error);
}

/**
 * Manually capture a message
 * @param message - Message to capture
 * @param level - Severity level
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info'
) {
  Sentry.captureMessage(message, level);
}

export default Sentry;
